import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { modelKeys, toConnection } from "./models";
import { ApiError } from "./errors";
import type { ApiConnection } from "@/shared/types/models";

interface Attempt { id: string; authorization_url: string; user_code: string; interval: number; expires_in: number; deadline: number }
type Poll = { status: "pending"; interval: number } | { status: "connected"; connection: ApiConnection };
const oauthKeys = { poll: (id: string) => ["provider-oauth", "codex", id] as const };

async function startSignIn(body: { name: string; connection_id?: string }): Promise<Attempt> {
  const data = unwrap(await client.post<Envelope<Omit<Attempt, "deadline">>>("/providers/oauth/codex", body));
  return { ...data, deadline: Date.now() + data.expires_in * 1000 };
}
async function pollSignIn(id: string, signal: AbortSignal): Promise<Poll> {
  return unwrap(await client.post<Envelope<Poll>>(`/providers/oauth/codex/${id}`, undefined, { signal }));
}
async function cancelSignIn(id: string): Promise<void> { await client.delete(`/providers/oauth/codex/${id}`); }
const terminal = (error: Error | null) => error instanceof ApiError && [400, 403, 404, 409, 422].includes(error.status);

export function useCodexSignIn() {
  const qc = useQueryClient();
  const pending = useRef<string | null>(null);
  const mounted = useRef(true);
  const [expired, setExpired] = useState(false);
  const start = useMutation({
    mutationFn: async (body: { name: string; connection_id?: string }) => {
      const attempt = await startSignIn(body);
      if (mounted.current) pending.current = attempt.id;
      else await cancelSignIn(attempt.id);
      return attempt;
    }, gcTime: 0, meta: { silent: true },
  });
  const cancel = useMutation({ mutationFn: cancelSignIn, gcTime: 0, meta: { silent: true } });
  const attempt = start.data;
  const poll = useQuery({
    queryKey: oauthKeys.poll(attempt?.id ?? ""),
    queryFn: ({ signal }) => pollSignIn(attempt!.id, signal),
    enabled: !!attempt && !expired && !cancel.isPending,
    retry: false, gcTime: 0, meta: { silent: true }, refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => query.state.data?.status === "connected" || terminal(query.state.error)
      ? false : (query.state.data?.status === "pending" ? query.state.data.interval : attempt?.interval ?? 5) * 1000,
  });
  useEffect(() => {
    setExpired(false);
    if (!attempt) return;
    const timer = setTimeout(() => setExpired(true), Math.max(0, attempt.deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [attempt]);
  useEffect(() => {
    if (poll.data?.status === "connected") {
      pending.current = null;
      void qc.invalidateQueries({ queryKey: modelKeys.all });
    }
  }, [poll.data, qc]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (pending.current) void cancelSignIn(pending.current).catch(() => {
        // Abandoned attempts expire server-side even when the browser is offline.
      });
    };
  }, []);
  return {
    start, cancel, poll, expired, stopped: terminal(poll.error),
    connection: poll.data?.status === "connected" ? toConnection(poll.data.connection) : null,
    reset: async () => {
      if (attempt) {
        await qc.cancelQueries({ queryKey: oauthKeys.poll(attempt.id) });
        await cancel.mutateAsync(attempt.id);
        qc.removeQueries({ queryKey: oauthKeys.poll(attempt.id) });
      }
      pending.current = null; start.reset(); cancel.reset(); setExpired(false);
    },
  };
}
